import { MapPin, Phone, Mail, Clock, ArrowRight, MessageCircle } from "lucide-react";
import { Button } from "@/components/ui/button";
import { useLang } from "@/i18n/LanguageContext";
import { useGoTo } from "@/lib/navigation";

const QUICK_HREFS = ["/services", "#why-us", "#testimonials", "#book", "#faq", "/resources"];

export default function Footer() {
  const { t, lang } = useLang();
  const f = t.footer;
  const scrollTo = useGoTo();

  return (
    <footer id="contact" data-testid="site-footer" className="bg-ink-900 text-white relative">
      {/* CTA strip */}
      <div className="border-b border-white/10">
        <div className="container-x py-14 sm:py-16 flex flex-col lg:flex-row gap-8 lg:items-center lg:justify-between">
          <div>
            <p className="text-overline mb-3" style={{ color: "#F8AE3E" }}>{f.ready}</p>
            <h3 className="font-display font-extrabold text-2xl sm:text-3xl lg:text-4xl text-white max-w-2xl leading-tight tracking-tight">
              {f.cta}
            </h3>
          </div>
          <Button
            data-testid="footer-book-cta"
            onClick={() => scrollTo("#book")}
            className="group bg-brand-500 text-white hover:bg-brand-600 hover:-translate-y-0.5 transition-all duration-300 rounded-sm h-12 px-7 text-sm tracking-wide font-semibold self-start lg:self-auto shadow-sm"
          >
            {f.ctaBtn}
            <ArrowRight className="w-4 h-4 ml-2 transition-transform group-hover:translate-x-1" />
          </Button>
        </div>
      </div>

      {/* Main footer */}
      <div className="container-x py-16 grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-12 gap-10">
        <div className="lg:col-span-4">
          <img src="/logo-dark.png" alt="Standard Accounting & Tax Advisory" className="h-12 w-auto" />
          <p className="mt-5 text-sm text-white/60 leading-relaxed max-w-sm">{f.blurb}</p>
        </div>

        <div className="lg:col-span-3">
          <h4 className="text-overline mb-5" style={{ color: "#F8AE3E" }}>{f.contact}</h4>
          <ul className="space-y-4 text-sm text-white/75">
            <li className="flex items-start gap-3">
              <MapPin className="w-4 h-4 text-brand-500 mt-0.5 shrink-0" />
              <span>{f.address1}<br />{f.address2}</span>
            </li>
            <li className="flex items-center gap-3">
              <Phone className="w-4 h-4 text-brand-500 shrink-0" />
              <a href="tel:+61433022898" data-testid="footer-phone-link" className="hover:text-brand-400 transition-colors">
                +61 433 022 898
              </a>
            </li>
            <li className="flex items-center gap-3">
              <Mail className="w-4 h-4 text-brand-500 shrink-0" />
              <a
                href="mailto:samsun@standardaccounting.com.au"
                data-testid="footer-email-link"
                className="hover:text-brand-400 transition-colors break-all"
              >
                samsun@standardaccounting.com.au
              </a>
            </li>
            <li className="flex items-start gap-3">
              <Clock className="w-4 h-4 text-brand-500 mt-0.5 shrink-0" />
              <span>{f.hours}</span>
            </li>
            {lang === "zh" && (
              <li data-testid="footer-wechat" className="flex items-start gap-3 pt-2">
                <MessageCircle className="w-4 h-4 text-brand-500 mt-0.5 shrink-0" />
                <div>
                  <div className="mb-2">{f.wechat}</div>
                  <img src="/wechat-qr.png" alt="微信二维码" className="w-28 h-28 bg-white p-1.5" loading="lazy" />
                </div>
              </li>
            )}
          </ul>
        </div>

        <div className="lg:col-span-2">
          <h4 className="text-overline mb-5" style={{ color: "#F8AE3E" }}>{f.quick}</h4>
          <ul className="space-y-3 text-sm">
            {f.quickLinks.map((label, i) => (
              <li key={i}>
                <button
                  onClick={() => scrollTo(QUICK_HREFS[i])}
                  data-testid={`footer-quick-${QUICK_HREFS[i].replace(/^[#/]/, "")}`}
                  className="text-white/70 hover:text-brand-400 transition-colors text-left"
                >
                  {label}
                </button>
              </li>
            ))}
          </ul>
        </div>

        <div className="lg:col-span-3">
          <h4 className="text-overline mb-5" style={{ color: "#F8AE3E" }}>{f.services}</h4>
          <ul className="space-y-3 text-sm">
            {f.serviceLinks.map((s, i) => (
              <li key={i}>
                <button
                  onClick={() => scrollTo("/services")}
                  className="text-white/70 hover:text-brand-400 transition-colors text-left"
                >
                  {s}
                </button>
              </li>
            ))}
          </ul>
        </div>
      </div>

      <div className="border-t border-white/10">
        <div className="container-x py-6 flex flex-col sm:flex-row items-start sm:items-center justify-between gap-3 text-xs text-white/50">
          <div>© {new Date().getFullYear()} {f.rights}</div>
          <div>{f.tagline}</div>
        </div>
      </div>
    </footer>
  );
}
